import React, { useEffect, useRef, useState } from "react";
import { createUserSocket } from "../utils/userSocket";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import { methodLabelFor } from "../config/market";
import OrderConfirmation from "../components/Order/OrderConfirmation";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { resetCart, savePaymentMethod } from "../redux/cartRedux";
import { resetOrderCreation } from "../redux/createOrderAction";
import { resetShippingCost } from "../redux/shippingCostRedux";

import {
  clearAirtelSSEData,
  setAirtelSSEData,
  setAirtelTrackOrderSSEData,
  verifyAirtelPayment,
} from "../redux/airtelSseRedux.js";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { userRequest } from "../api/requestMethods";
import useBlockBackNavigation from "../hooks/useBlockBackNavigation";
import { goToSignIn } from "../utils/signInReturn";

const AirtelPayScreen = () => {
  const user = useSelector((state) => state.user.currentUser);
  const { airtelSseData, trackOrderAirtelSse, verificationStatus } =
    useSelector((state) => state.airtelSse);

  const userId = user?.result?._id || user?._id;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!user) {
      goToSignIn(history);
    }
  }, [user, history]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const respObj = params.get("resp");

    // Send respObj to the server for verification
    if (respObj) {
      dispatch(verifyAirtelPayment({ userId, respObj }))
        .unwrap()
        .then((response) => {
          // `settled`, NOT `response.data.status`. That nested field is
          // Flutterwave's ENVELOPE status — it reads "success" whenever the
          // verify CALL worked, regardless of what happened to the money. So
          // the cart was being emptied and a success toast shown for a charge
          // that had only been accepted, not paid. `settled` is computed in the
          // thunk from the HTTP status (200 settled / 202 still in flight).
          if (response?.settled) {
            dispatch(resetCart());
            toast.success("Payment confirmed.");
          }
          // pending → say nothing yet; the poll below resolves it.
        })
        .catch((error) => {
          toast.error(error);
        });
    }
  }, [location.search, userId, dispatch]);

  // ── Terminal-state watchdog ───────────────────────────────────────────────
  //
  // This page had NO poll and NO deadline — it waited on a socket alone. When
  // the socket never delivered (the customer's Flutterwave page died, the
  // webhook was lost, the tab slept), the screen sat on "Verifying your
  // payment" indefinitely with no way forward and no idea whether money had
  // moved. That is the web twin of the incident the mobile WebViews were fixed
  // for, and the socket is precisely what is down whenever a fallback is
  // needed.
  //
  // `/fw/payment-status` can now report a real failure (it used to be a mirror
  // of isPaid and could only ever answer "successful" or "pending"), and it
  // actively re-verifies with Flutterwave once an authorisation has been quiet
  // for a while — so this poll is an oracle rather than a hopeful retry.
  //
  // The deadline does NOT decide the payment. Timing out means "we stopped
  // watching"; the server keeps verifying and flwReconciliationSweeper runs
  // behind it. Never invite a second payment.
  const POLL_MS = 4000;
  const DEADLINE_MS = 240000;
  const [pollState, setPollState] = useState({ slow: false, timedOut: false, failed: null, authExpired: false });
  const [manualApproval, setManualApproval] = useState(null);
  const settledRef = useRef(false);

  // The watchdog must not depend on the snapshot fetch succeeding. That request
  // fires once, swallows its errors, and is the ONLY source of
  // trackOrderAirtelSse — so if it failed or returned [], there was no poll, no
  // deadline and no timeout, and the page sat on "Waiting for your approval"
  // forever: exactly the failure this was written to eliminate. The created
  // order's id is a second, independent source.
  // Slice key is `orders` (store.js: `orders: orderReducer`) and the document
  // lives at `.order`, which starts as "" — so optional chaining is doing real
  // work here, not decoration.
  const createdOrderId = useSelector((s) => s.orders?.order?._id || null);
  const trackedOrderId = trackOrderAirtelSse?._id || createdOrderId;
  useEffect(() => {
    if (!trackedOrderId || trackOrderAirtelSse?.isPaid) return undefined;
    let cancelled = false;
    const startedAt = Date.now();

    const timer = setInterval(async () => {
      if (cancelled || settledRef.current) return;
      const elapsed = Date.now() - startedAt;

      if (elapsed >= DEADLINE_MS) {
        clearInterval(timer);
        setPollState((p) => ({ ...p, timedOut: true }));
        return;
      }

      try {
        const { data } = await userRequest(`/fw/payment-status/${trackedOrderId}`);
        if (cancelled || !data) return;
        if (data.slow) setPollState((p) => ({ ...p, slow: true }));
        // Rail-specific recovery steps from the server. On WEB this matters
        // more than on mobile: the customer was sent to Flutterwave by a full
        // page navigation, so there was no way to show them anything at all
        // while they waited — this page is the first moment we can speak again.
        if (data.manualApproval) setManualApproval(data.manualApproval);

        if (data.status === "successful") {
          settledRef.current = true;
          clearInterval(timer);
          dispatch(setAirtelSSEData({ data: { ...data, status: "successful" } }));
          dispatch(resetCart());
        } else if (data.status === "failed") {
          settledRef.current = true;
          clearInterval(timer);
          // The processor's own reason, never our guess.
          setPollState((p) => ({
            ...p,
            failed: data.reason || "The payment was not completed.",
          }));
        }
      } catch (e) {
        // A 401 is NOT a payment problem, and must not be swallowed. The mobile
        // client silently refreshes on 401 (api/client.js monitor); the web has
        // no response interceptor, so without this the poll would keep ticking
        // with a dead token until the deadline and then report "we haven't
        // heard back" — turning an expired session into what looks like a
        // failed payment. Stop, and say the true thing.
        if (e?.response?.status === 401) {
          clearInterval(timer);
          setPollState((p) => ({ ...p, authExpired: true }));
          return;
        }
        // Anything else: a single blip must not end the wait — the deadline
        // above guarantees this loop terminates.
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [trackedOrderId, trackOrderAirtelSse?.isPaid, dispatch]);

  // Back must not return into the checkout flow (double-order protection).
  useBlockBackNavigation("/");

  // The order this page is tracking (used to filter room-wide socket events).
  const trackedOrderIdRef = useRef(null);
  useEffect(() => {
    trackedOrderIdRef.current = trackOrderAirtelSse?._id || null;
  }, [trackOrderAirtelSse?._id]);

  // ── Initial order snapshot ────────────────────────────────────────────────
  // The legacy `/fw/events` + `/mobile/:id/stream` SSE channels delivered the
  // customer's latest order document; with sockets (push-only) we fetch it
  // once via REST — the customer lands here straight after paying, so their
  // newest order IS this one — then live updates arrive over the socket.
  useEffect(() => {
    if (!userId || trackOrderAirtelSse?._id) return;
    userRequest(`/mobile/${userId}/myorders`)
      .then((r) => {
        const latest = Array.isArray(r.data) ? r.data[0] : null;
        if (latest) dispatch(setAirtelTrackOrderSSEData(latest));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dispatch]);

  // ── Live payment + order status over Socket.IO ───────────────────────────
  // Replaces BOTH legacy SSE channels:
  //  • `/fw/events` (per-process globals — broken across dynos) → join
  //    `payment:${client_id}`; the Flutterwave webhook/verify path emits
  //    `paymentStatusUpdate` {orderId, tx_ref, flw_ref, status,
  //    processor_response, amount} (flutterWaveController.emitPaymentStatus).
  //  • `/mobile/:id/stream` (stale-snapshot order SSE) → join
  //    `orderRoom:${client_id}`; the Mongo change stream emits `orderUpdate`
  //    (full order doc) on every order write — filtered to THIS order.
  useEffect(() => {
    if (!userId) return;

    // Carries the customer's token: their rooms need it (utils/userSocket.js).
    const socket = createUserSocket();

    socket.on("connect", () => {
      console.log("[AirtelPay] Socket connected, joining payment + order rooms…");
      socket.emit("join", `payment:${userId}`);
      socket.emit("join", `orderRoom:${userId}`);
    });

    socket.on("paymentStatusUpdate", (payload) => {
      console.log("[AirtelPay] paymentStatusUpdate:", payload?.status, payload?.tx_ref);
      // Preserve the legacy `airtelSseData.data.*` accessor shape the render uses.
      dispatch(setAirtelSSEData({ data: payload }));
    });

    socket.on("orderUpdate", (updatedOrder) => {
      if (
        trackedOrderIdRef.current &&
        String(updatedOrder?._id) !== String(trackedOrderIdRef.current)
      )
        return;
      console.log(
        "[AirtelPay] orderUpdate:",
        updatedOrder?._id,
        updatedOrder?.delivery_status
      );
      dispatch(setAirtelTrackOrderSSEData(updatedOrder));
    });

    return () => {
      socket.off("paymentStatusUpdate");
      socket.off("orderUpdate");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  const handleContinueShopping = () => {
    dispatch(resetCart());
    dispatch(resetOrderCreation());
    dispatch(resetShippingCost());
    dispatch(clearAirtelSSEData());

    history.push("/");
  };





  // ── What we actually know ─────────────────────────────────────────────────
  // Five states, because collapsing them is what stranded people. The old two
  // ("loading" / "failed", everything else = verified) meant a charge that was
  // merely ACCEPTED rendered as "Payment verified", and a charge that never
  // resolved rendered as "Verifying…" forever.
  const paid =
    trackOrderAirtelSse?.isPaid === true ||
    airtelSseData?.data?.status === "successful";
  const verifyFailed = verificationStatus === "failed" || !!pollState.failed;
  const timedOut = (pollState.timedOut || pollState.authExpired) && !paid && !verifyFailed;
  const state = paid ? "ok" : verifyFailed ? "failed" : timedOut ? "unresolved" : "loading";

  const title = paid
    ? "Payment verified"
    : verifyFailed
      ? "This payment did not go through"
      : timedOut
        ? "We haven't heard back yet"
        : "Waiting for your approval";

  const note = paid
    ? airtelSseData?.data?.processor_response ||
      // Screen serves EVERY Flutterwave mobile-money network (UG Airtel;
      // ZM MTN/Airtel/Zamtel) — keep the confirmation network-neutral.
      "Mobile money confirmed this transaction."
    : verifyFailed
      ? // Flutterwave's own words where we have them. The old copy assumed
        // money HAD left the account, which for a declined charge is wrong and
        // sends the customer to support for no reason.
        `${pollState.failed || "The payment was not completed."} You have not been charged. ` +
        `If money did leave your account, contact support with your order number.`
      : timedOut
        ? // NOT a failure — we stopped watching, the server has not. Never
          // invite a second payment.
          (pollState.authExpired
            ? "Your session expired while we were confirming. Sign in and check My Orders — " +
              "if you approved the payment it will still have gone through, so don't pay again."
            : "If you approved the payment it will still go through. Check My Orders in a " +
              "few minutes before paying again — you may already have been charged.")
        : pollState.slow
          ? "This is taking longer than usual. Keep this page open — we'll confirm as soon as your provider responds."
          : "Approve the payment on your phone. This usually takes a few seconds.";

  return (
    <>
      <Navbar />
      <OrderConfirmation
        order={trackOrderAirtelSse}
        paymentLabel={methodLabelFor(trackOrderAirtelSse?.paymentMethod) || "Mobile Money"}
        onContinueShopping={handleContinueShopping}
        paymentSlot={
          <VerifyRow $state={state}>
            <VerifyDot aria-hidden="true" />
            <div>
              <VerifyTitle>{title}</VerifyTitle>
              <VerifyNote>{note}</VerifyNote>
              {/* Only shown while the authorisation could still be acted on,
                  and only for a rail that HAS a recovery path. Airtel returns
                  supported:false with honest "start again" copy rather than a
                  USSD walkthrough for a menu it doesn't have. */}
              {manualApproval && !paid && !verifyFailed && (
                <ApprovalBox>
                  <ApprovalTitle>{manualApproval.title}</ApprovalTitle>
                  {manualApproval.steps.map((step, i) => (
                    <ApprovalStep key={i}>
                      {manualApproval.supported ? `${i + 1}. ` : "• "}
                      {step}
                    </ApprovalStep>
                  ))}
                  {!!manualApproval.note && <ApprovalNote>{manualApproval.note}</ApprovalNote>}
                  {manualApproval.action === "restart" && (
                    <RestartBtn
                      onClick={() => {
                        // Pre-select the rail they already chose. This DISPATCHES
                        // rather than passing a query param, because
                        // PlaceOrderScreen reads paymentMethod from the store —
                        // a `?rail=` in the URL would be inert and the button
                        // would look broken.
                        dispatch(savePaymentMethod(manualApproval.rail));
                        history.push("/placeorder");
                      }}
                    >
                      {manualApproval.retryLabel || "Start the payment again"}
                    </RestartBtn>
                  )}
                </ApprovalBox>
              )}
              {trackOrderAirtelSse?.transactionId && (
                <VerifyNote>
                  Transaction reference: {trackOrderAirtelSse.transactionId}
                </VerifyNote>
              )}
            </div>
          </VerifyRow>
        }
      />
    </>
  );
};

const VerifyRow = styled.div`
  color: ${(p) =>
    p.$state === "loading"
      ? "#8a9996"
      : p.$state === "failed"
        ? "#c0483a"
        : p.$state === "unresolved"
          ? "#b8860b" /* amber — not settled, but NOT a failure either */
          : "#5d8f24"};
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const VerifyDot = styled.span`
  flex: none;
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const VerifyTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #14211f;
`;

const ApprovalBox = styled.div`
  margin-top:12px;background:#f2f6f5;border-radius:8px;padding:12px;
`;
const ApprovalTitle = styled.div`font-weight:700;font-size:14px;color:#14211f;margin-bottom:6px;`;
const ApprovalStep = styled.div`font-size:13px;line-height:20px;color:#4a5a57;`;
const ApprovalNote = styled.div`font-size:12px;color:#8a9996;margin-top:6px;font-style:italic;`;
const RestartBtn = styled.button`
  margin-top:10px;border:none;border-radius:8px;padding:10px 16px;cursor:pointer;
  background:#324D54;color:#fff;font-size:14px;font-weight:600;
`;

const VerifyNote = styled.div`
  font-size: 13px;
  color: #4a5a57;
  margin-top: 2px;
`;

export default AirtelPayScreen;
